
import { mapGetters } from 'vuex';

// API
import * as apiContributions from '../api/private/contributions';

import errorsMixin from '../mixins/error-handler';

export default {
  name: 'SDetailFacilities',
  mixins: [errorsMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
    facilities: {
      type: Array,
      required: true,
    },
    hasContribution: {
      type: Boolean,
      default: false,
    },
    contributions: {
      type: Object,
      default: null,
    },
    loadingContributions: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Object,
      default: () => ({}),
    },
    hideAppBar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fieldsRefs: ['contribution-playground', 'contribution-facilities'],
      editFacilities: false,
      waitEditFacilities: false,
      newFacilities: null,
      contributionValue: null,
      toggleTooltip: false,
      radioValue: [true, false, null],
      loading: false,
      showDeleteContribution: false,
    };
  },
  computed: {
    ...mapGetters({
      isAuth: 'authToken/isAuth',
      userSecured: 'preferences/userSecured',
    }),
    hasAvailableFacilities() {
      return this.facilities.some((facility) => facility.value === true);
    },
    noFacilities() {
      return this.facilities.every((facility) => facility.value === false);
    },
    orderedFacilities() {
      return [...this.facilities].sort((a, b) => b.value - a.value);
    },
    commoditiesEmpty() {
      const empty = [
        'Aucune commodité renseignée pour le moment.',
        'Les commodités n\'ont pas été renseignées.',
        'Il manque des informations sur les commodités de cette aire de jeux.',
        'Les commodités ne nous ont pas encore été communiquées pour cette aire de jeux.',
        'Les commodités sont manquantes.',
        'Pour le moment, nous n\'avons pas connaissance des commodités présentes sur l\'aire de jeux.',
      ];

      const cta = [
        'Soyez le premier à nous les indiquer !',
        'Aidez-nous à compléter ces informations en renseignant les commodités.',
        'Si vous savez quelles commodités se trouvent sur cette aire de jeux, n\'hésitez pas à nous les communiquer.',
        'Gagnez des niveaux de contributeur en complétant les commodités !',
        'Contribuez en nous indiquant les commodités de cette aire de jeux.',
        'Votre aide est la bienvenue pour renseigner cette information manquante.',
        'Merci de nous aider à compléter cette fiche en remplissant les commodités.',
        'Vous connaissez l\'aire de jeux ? Partagez ce que vous savez avec la communauté !',
      ];

      return `${empty[Math.floor(Math.random() * empty.length)]} ${cta[Math.floor(Math.random() * cta.length)]}`;
    },
  },
  watch: {
    id() {
      this.closeFacilitiesForm();
    },
    editFacilities(editFacilities) {
      if (!editFacilities) {
        // reset errors when leaving edition
        this.errors$.reset();
        // deactivate hideOnScroll
        this.$emit('update:hide-app-bar', false);
        setTimeout(() => {
          if (!this.visible.facilities) {
            // scroll to the top of the "facilities" block if it is not visible
            this.$refs.detailFacilities.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setTimeout(() => {
              // reactivate hideOnScroll
              this.$emit('update:hide-app-bar', true);
            }, 500);
          } else {
            // reactivate hideOnScroll
            this.$emit('update:hide-app-bar', true);
          }
        }, 500);
      }
      this.$emit('has-unsave', editFacilities);
    },
    isAuth(value) {
      if (!value) {
        this.editFacilities = false;
      }
    },
    hasContribution() {
      this.contributionValue = this.hasContribution;
    },
    loadingContributions() {
      if (!this.loadingContributions && this.userSecured && this.waitEditFacilities) {
        // After logging in, call the API to get the contribution data
        if (this.contributionValue && this.contributions === null) {
          this.$emit('get-contributions');
          return;
        }
        this.setFacilities();
        this.editFacilities = true;
        this.waitEditFacilities = false;
      }
    },
  },
  mounted() {
    this.contributionValue = this.hasContribution;
  },
  methods: {
    showLogin() {
      if (!this.userSecured) {
        this.$emit('login');
        this.waitEditFacilities = true;
      }
    },
    showFacilitiesForm() {
      this.showLogin();
      if (!this.waitEditFacilities) {
        // If they are not yet set, call the API to get the contribution data
        if (!this.newFacilities && this.contributionValue && this.contributions === null) {
          this.waitEditFacilities = true;
          this.$emit('get-contributions');
          return;
        }
        this.setFacilities();
        this.editFacilities = true;
      }
    },
    closeFacilitiesForm() {
      this.editFacilities = false;
      this.waitEditFacilities = false;
      this.newFacilities = null;
    },
    setFacilities() {
      this.newFacilities = [];
      this.facilities.forEach((facility) => {
        const newFacility = { ...facility };
        const newFacilityValue = this.contributions?.facilities && this.contributions.facilities[facility.id] !== null ? this.contributions.facilities[facility.id] : facility.value;
        newFacility.value = this.radioValue.indexOf(newFacilityValue);
        this.newFacilities.push(newFacility);
      });
    },
    editFacilitiesForm() {
      this.errors$.reset();

      this.loading = true;
      const data = {
        playground: this.id,
        facilities: {
          has_close: this.radioValue[this.newFacilities.find((facility) => facility.id === 'has_close').value],
          has_picnic: this.radioValue[this.newFacilities.find((facility) => facility.id === 'has_picnic').value],
          has_seat: this.radioValue[this.newFacilities.find((facility) => facility.id === 'has_seat').value],
          has_shady: this.radioValue[this.newFacilities.find((facility) => facility.id === 'has_shady').value],
          has_trash: this.radioValue[this.newFacilities.find((facility) => facility.id === 'has_trash').value],
          has_water: this.radioValue[this.newFacilities.find((facility) => facility.id === 'has_water').value],
          has_wc: this.radioValue[this.newFacilities.find((facility) => facility.id === 'has_wc').value],
        },
      };
      apiContributions.contribution(data, this).then(() => {
        this.closeFacilitiesForm();
        this.contributionValue = true;
        this.$emit('contribution', data.facilities);
      }).catch((error) => {
        // No change on this field. Just close the form
        if (this.errors$.getCode('contribution', 'form') === 'no_change') {
          this.closeFacilitiesForm();
          return;
        }

        // Validation error
        if (error.response && error.response.status === 400) {
          if (this.errors$.hasErrors()) {
            this.errors$.focusFirstError();
            return;
          }
          this.errors$.handleError(true, 'contribution', 'form');
          this.$nextTick(() => {
            this.$refs['contribution-form'].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    deleteContribution() {
      this.closeFacilitiesForm();
      this.contributionValue = false;
      this.$emit('delete-contribution');
    },
  },
};
